// ============================================================================
// MÓDULO: ConsentBuilder.jsx — v1.0 — Gerador de Consentimento Informado
// ARQUIVO: src/features/petitions/ConsentBuilder.jsx
// OBJETIVO: Gerar documento de consentimento informado vinculado a uma petição.
//           O cliente revisa e aceita/recusa no portal.
// VINCULO:  Blueprint PESQUISAS_PROFUNDAS/BLUEPRINT_CONSENTIMENTO_INFORMADO_PETICOES.md
// ============================================================================
import React, { useCallback, useState } from 'react';
import { auth } from '../../shared/api/firebase/config';
import useCurrentUser from '../../hooks/useCurrentUser';
import {
  createConsentDocument,
  linkConsentToPetition,
  logConsentToClientNotes,
} from '../../services/ConsentBuilderRepository';
import {
  AlertTriangle, CheckCircle2, ChevronDown, ChevronUp,
  FileText, Loader2, Send, ShieldCheck, X
} from 'lucide-react';

// ── Default form ─────────────────────────────────────────────────────────────
const DEFAULT_CONSENT_FORM = {
  resumoPeticao: '',
  pedidos: [''],
  riscos: [''],
  alternativas: [''],
  observacoes: '',
  iaDisclosure: false,
  iaDetails: '',
};

// ── Build HTML for consent document ──────────────────────────────────────────
function buildConsentHtml({ clientName, petitionTitle, form, lawyerName, oab }) {
  const esc = (s) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const pedidosHtml = (form.pedidos || []).filter(p => p.trim()).map((p, i) =>
    `<li>${esc(p)}</li>`
  ).join('\n');
  const riscosHtml = (form.riscos || []).filter(r => r.trim()).map((r) =>
    `<li>${esc(r)}</li>`
  ).join('\n');
  const alternativasHtml = (form.alternativas || []).filter(a => a.trim()).map((a) =>
    `<li>${esc(a)}</li>`
  ).join('\n');

  const dataFormatada = new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });

  return `<!doctype html>
<html lang="pt-BR">
<head>
  <meta charset="utf-8"/>
  <title>Consentimento Informado — ${esc(petitionTitle)}</title>
  <style>
    @page { size: A4; margin: 25mm 20mm; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 11pt; line-height: 1.6; color: #1a1a1a; margin: 0; padding: 25mm 20mm; }
    h1 { text-align: center; font-size: 14pt; font-weight: bold; color: #20243B; margin: 0 0 1.5em; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid #C5A059; padding-bottom: 8px; }
    h2 { font-size: 11pt; font-weight: bold; color: #20243B; margin: 1.5em 0 0.5em; text-transform: uppercase; }
    p { text-align: justify; margin: 0 0 0.8em; }
    ul { margin: 0 0 1em 1.5em; padding: 0; }
    li { margin-bottom: 0.4em; }
    .header-office { text-align: center; font-size: 9pt; color: #666; margin-bottom: 2em; }
    .header-office strong { color: #20243B; font-size: 10pt; }
    .box { background: #f8f9fa; border: 1px solid #e5e7eb; border-left: 4px solid #C5A059; padding: 12px 16px; margin: 1em 0; border-radius: 4px; }
    .box-alert { border-left-color: #dc2626; background: #fef2f2; }
    .box-info { border-left-color: #2563eb; background: #eff6ff; }
    .signature-area { margin-top: 3em; text-align: center; }
    .signature-area .line { border-top: 1px solid #333; display: inline-block; width: 60%; margin-bottom: 4px; }
    .footer-note { font-size: 8pt; color: #999; text-align: center; margin-top: 3em; border-top: 1px solid #e5e7eb; padding-top: 1em; }
    .ia-badge { display: inline-block; background: #fef3c7; color: #92400e; border: 1px solid #fbbf24; border-radius: 4px; padding: 2px 8px; font-size: 9pt; font-weight: bold; margin: 0.5em 0; }
  </style>
</head>
<body>
  <div class="header-office">
    <strong>RODRIGO DEMETERCO ADVOCACIA</strong><br/>
    OAB/PR nº 113.938 · rodrigo@rodrigodemeterco.adv.br<br/>
    Curitiba/PR · Portal Jurídico RDA
  </div>

  <h1>Consentimento Informado</h1>

  <p><strong>Cliente:</strong> ${esc(clientName)}</p>
  <p><strong>Peça Processual de Referência:</strong> ${esc(petitionTitle)}</p>
  <p><strong>Data:</strong> ${esc(dataFormatada)}</p>

  ${form.iaDisclosure ? `<p class="ia-badge">⚠️ Esta peça foi elaborada com auxílio de Inteligência Artificial (OAB Rec. 001/2024)</p>` : ''}
  ${form.iaDisclosure && form.iaDetails ? `<p><em>${esc(form.iaDetails)}</em></p>` : ''}

  <h2>1. Resumo da Peça</h2>
  <div class="box">
    <p>${esc(form.resumoPeticao).replace(/\n/g, '</p><p>')}</p>
  </div>

  <h2>2. Pedidos Formulados</h2>
  ${pedidosHtml ? `<ul>${pedidosHtml}</ul>` : '<p><em>Nenhum pedido especificado.</em></p>'}

  <h2>3. Riscos e Possíveis Desfechos</h2>
  <div class="box box-alert">
    ${riscosHtml ? `<ul>${riscosHtml}</ul>` : '<p><em>Nenhum risco identificado.</em></p>'}
  </div>

  <h2>4. Alternativas Consideradas</h2>
  <div class="box box-info">
    ${alternativasHtml ? `<ul>${alternativasHtml}</ul>` : '<p><em>Nenhuma alternativa registrada.</em></p>'}
  </div>

  ${form.observacoes.trim() ? `
  <h2>5. Observações do Advogado</h2>
  <p>${esc(form.observacoes).replace(/\n/g, '</p><p>')}</p>
  ` : ''}

  <h2>${form.observacoes.trim() ? '6' : '5'}. Declaração de Consentimento</h2>
  <p>Declaro que fui informado(a) sobre o conteúdo, os riscos e as alternativas da peça processual acima referenciada. Compreendi as explicações fornecidas pelo advogado e <strong>autorizo o prosseguimento</strong> do protocolo judicial da petição, nos termos da Lei nº 13.709/2018 (LGPD) e do Estatuto da Advocacia (Lei nº 8.906/1994).</p>
  <p>Este consentimento é livre, informado e revogável mediante manifestação expressa ao escritório.</p>

  <div class="signature-area">
    <p style="margin-top:3em;"><span class="line"></span></p>
    <p><strong>${esc(clientName)}</strong></p>
    <p style="font-size:9pt;color:#666;">Aceite registrado eletronicamente via Portal do Cliente RDA</p>
  </div>

  <div class="footer-note">
    Documento gerado pelo Portal Jurídico RDA · ${esc(lawyerName)} · ${esc(oab)}<br/>
    Conformidade: LGPD (Lei 13.709/2018), OAB Recomendação 001/2024, Lei 14.063/2020
  </div>
</body>
</html>`;
}

// ── Main Component ───────────────────────────────────────────────────────────
export const InformedConsentFactory = function ConsentBuilder({
  petitionDocId,
  petitionTitle,
  clientId,
  clientName,
  cadernoId,
  cadernoTitle,
  pieceId,
  onClose,
  onSuccess,
}) {
  const { displayName, email } = useCurrentUser();
  const [form, setForm] = useState({ ...DEFAULT_CONSENT_FORM });
  const [isPublishing, setIsPublishing] = useState(false);
  const [notice, setNotice] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const updateField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  // --- List field handlers ---
  const handleAddItem = (key) => setForm(prev => ({ ...prev, [key]: [...prev[key], ''] }));
  const handleUpdateItem = (key, idx, val) => setForm(prev => {
    const arr = [...prev[key]];
    arr[idx] = val;
    return { ...prev, [key]: arr };
  });
  const handleRemoveItem = (key, idx) => setForm(prev => {
    const arr = prev[key].filter((_, i) => i !== idx);
    return { ...prev, [key]: arr.length === 0 ? [''] : arr };
  });

  // --- Validation ---
  const isValid = form.resumoPeticao.trim().length > 10;

  // --- Preview HTML ---
  const previewHtml = buildConsentHtml({
    clientName: clientName || 'Cliente',
    petitionTitle: petitionTitle || 'Petição',
    form,
    lawyerName: 'Rodrigo Demeterco',
    oab: 'OAB/PR 113.938',
  });

  // --- Publish to Firestore ---
  const handlePublish = useCallback(async () => {
    if (isPublishing || !isValid) return;
    setIsPublishing(true);
    setNotice('');

    try {
      const actorName = displayName || 'Rodrigo Demeterco';
      const consentName = `Consentimento Informado — ${petitionTitle || 'Petição'}`;

      const docData = {
        clientId: clientId || null,
        clientName: clientName || null,
        type: 'INFORMED_CONSENT',
        category: 'Consentimento Informado',
        classification: { category: 'Consentimento Informado' },
        name: consentName,
        title: consentName,
        parentDocumentId: petitionDocId || null,
        cadernoId: cadernoId || null,
        cadernoTitle: cadernoTitle || null,
        pieceId: pieceId || null,

        requiresConsent: true,
        consentStatus: 'pending_consent',
        clientVisible: true,
        status: 'PUBLISHED',

        htmlContent: previewHtml,
        consentSummary: {
          resumoPeticao: form.resumoPeticao.trim(),
          pedidos: form.pedidos.filter(p => p.trim()),
          riscos: form.riscos.filter(r => r.trim()),
          alternativas: form.alternativas.filter(a => a.trim()),
          observacoes: form.observacoes.trim(),
          iaDisclosure: form.iaDisclosure,
          iaDetails: form.iaDetails.trim(),
        },

        publication: {
          source: 'consent_builder',
          publishType: 'informed_consent',
          format: 'html',
          version: 1,
          publishedAtIso: new Date().toISOString(),
          publishedBy: actorName,
        },

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: email || null,
      };

      // Create consent document
      const consentDocId = await createConsentDocument(docData);

      // Link consent back to petition
      if (petitionDocId) {
        await linkConsentToPetition(petitionDocId, consentDocId);
      }

      // Log to client prontuário
      if (clientId) {
        try {
          await logConsentToClientNotes(clientId, consentName, petitionTitle, consentDocId, petitionDocId, actorName);
        } catch (noteErr) { console.warn('[RDA] Nota prontuário falhou:', noteErr); }
      }

      setNotice('✅ Consentimento informado publicado! O cliente pode aceitar no portal.');
      if (onSuccess) onSuccess({ consentDocId: consentDocId, petitionDocId });

    } catch (err) {
      setNotice(`❌ Erro: ${String(err?.message || err).slice(0, 150)}`);
    } finally {
      setIsPublishing(false);
    }
  }, [isPublishing, isValid, form, previewHtml, petitionDocId, petitionTitle, clientId, clientName, cadernoId, cadernoTitle, pieceId, onSuccess]);

  // ── RENDER ─────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="px-5 py-3 border-b border-[#C5A059]/40 bg-[#20243B] flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-[#C5A059]" />
            <h2 className="text-xs font-bold text-white uppercase tracking-widest">Consentimento Informado</h2>
          </div>
          <button type="button" onClick={onClose} className="w-7 h-7 rounded-full flex items-center justify-center text-gray-300 hover:text-white hover:bg-white/20">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Context bar */}
        <div className="px-5 py-2 bg-gray-50 border-b border-gray-200 flex items-center gap-4 text-[10px] shrink-0">
          <span className="text-gray-500"><strong className="text-[#20243B]">Cliente:</strong> {clientName || '—'}</span>
          <span className="text-gray-500"><strong className="text-[#20243B]">Petição:</strong> {petitionTitle || '—'}</span>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">

          {/* 1. Resumo */}
          <div>
            <label className="block text-[10px] font-bold uppercase text-gray-500 mb-1.5">
              1. Resumo da Petição (linguagem simples para o cliente)
            </label>
            <textarea
              value={form.resumoPeticao}
              onChange={e => updateField('resumoPeticao', e.target.value)}
              rows={4}
              placeholder="Ex: Estamos impetrando um Mandado de Segurança contra a CONAB para anular o ato administrativo que cancelou seu cadastro no programa de distribuição de alimentos..."
              className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:border-[#C5A059] outline-none resize-y"
            />
          </div>

          {/* 2. Pedidos */}
          <div>
            <label className="block text-[10px] font-bold uppercase text-gray-500 mb-1.5">2. Pedidos formulados</label>
            {form.pedidos.map((p, idx) => (
              <div key={idx} className="flex items-center gap-2 mb-1.5">
                <input
                  value={p}
                  onChange={e => handleUpdateItem('pedidos', idx, e.target.value)}
                  placeholder={`Pedido ${idx + 1}`}
                  className="flex-1 px-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:border-[#C5A059] outline-none"
                />
                {form.pedidos.length > 1 && (
                  <button type="button" onClick={() => handleRemoveItem('pedidos', idx)} className="text-red-400 hover:text-red-600 text-xs">✕</button>
                )}
              </div>
            ))}
            <button type="button" onClick={() => handleAddItem('pedidos')} className="text-[10px] font-bold text-[#C5A059] hover:text-[#20243B]">+ Adicionar pedido</button>
          </div>

          {/* 3. Riscos */}
          <div>
            <label className="flex items-center gap-1.5 text-[10px] font-bold uppercase text-gray-500 mb-1.5">
              <AlertTriangle className="w-3 h-3 text-red-500" /> 3. Riscos e possíveis desfechos desfavoráveis
            </label>
            {form.riscos.map((r, idx) => (
              <div key={idx} className="flex items-center gap-2 mb-1.5">
                <input
                  value={r}
                  onChange={e => handleUpdateItem('riscos', idx, e.target.value)}
                  placeholder={`Risco ${idx + 1}`}
                  className="flex-1 px-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:border-[#C5A059] outline-none"
                />
                {form.riscos.length > 1 && (
                  <button type="button" onClick={() => handleRemoveItem('riscos', idx)} className="text-red-400 hover:text-red-600 text-xs">✕</button>
                )}
              </div>
            ))}
            <button type="button" onClick={() => handleAddItem('riscos')} className="text-[10px] font-bold text-[#C5A059] hover:text-[#20243B]">+ Adicionar risco</button>
          </div>

          {/* 4. Alternativas */}
          <div>
            <label className="block text-[10px] font-bold uppercase text-gray-500 mb-1.5">4. Alternativas consideradas</label>
            {form.alternativas.map((a, idx) => (
              <div key={idx} className="flex items-center gap-2 mb-1.5">
                <input
                  value={a}
                  onChange={e => handleUpdateItem('alternativas', idx, e.target.value)}
                  placeholder={`Alternativa ${idx + 1}`}
                  className="flex-1 px-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:border-[#C5A059] outline-none"
                />
                {form.alternativas.length > 1 && (
                  <button type="button" onClick={() => handleRemoveItem('alternativas', idx)} className="text-red-400 hover:text-red-600 text-xs">✕</button>
                )}
              </div>
            ))}
            <button type="button" onClick={() => handleAddItem('alternativas')} className="text-[10px] font-bold text-[#C5A059] hover:text-[#20243B]">+ Adicionar alternativa</button>
          </div>

          {/* 5. Observações */}
          <div>
            <label className="block text-[10px] font-bold uppercase text-gray-500 mb-1.5">5. Observações do advogado (opcional)</label>
            <textarea
              value={form.observacoes}
              onChange={e => updateField('observacoes', e.target.value)}
              rows={3}
              placeholder="Ex: Conforme conversado em reunião de 15/05/2026, a cliente optou pela via judicial após esgotamento da via administrativa..."
              className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:border-[#C5A059] outline-none resize-y"
            />
          </div>

          {/* 6. IA Disclosure */}
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.iaDisclosure}
                onChange={e => updateField('iaDisclosure', e.target.checked)}
                className="accent-[#C5A059] w-4 h-4"
              />
              <span className="text-xs font-bold text-amber-800">
                Esta peça foi elaborada com auxílio de IA (OAB Recomendação 001/2024)
              </span>
            </label>
            {form.iaDisclosure && (
              <input
                value={form.iaDetails}
                onChange={e => updateField('iaDetails', e.target.value)}
                placeholder="Detalhes: ex. 'Pesquisa jurisprudencial assistida por IA, revisada integralmente pelo advogado.'"
                className="w-full px-3 py-1.5 text-xs border border-amber-300 rounded-lg focus:border-[#C5A059] outline-none bg-white"
              />
            )}
          </div>

          {/* Preview toggle */}
          <div>
            <button type="button" onClick={() => setShowPreview(!showPreview)}
              className="flex items-center gap-1.5 text-[10px] font-bold text-[#20243B] hover:text-[#C5A059] transition-colors">
              {showPreview ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              {showPreview ? 'Ocultar Prévia' : 'Ver Prévia do Documento'}
            </button>
            {showPreview && (
              <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden bg-white shadow-inner">
                <iframe
                  title="Consent Preview"
                  srcDoc={previewHtml}
                  className="w-full border-0"
                  style={{ minHeight: '400px' }}
                  sandbox="allow-same-origin"
                />
              </div>
            )}
          </div>

          {/* Notice */}
          {notice && (
            <div className={`text-xs font-semibold rounded-lg px-3 py-2 ${
              notice.startsWith('✅') ? 'text-emerald-700 bg-emerald-50 border border-emerald-200'
              : 'text-red-700 bg-red-50 border border-red-200'
            }`}>
              {notice}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between shrink-0">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-xs font-bold text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-100">
            Cancelar
          </button>
          <button
            type="button"
            onClick={handlePublish}
            disabled={isPublishing || !isValid}
            className="flex items-center gap-2 px-5 py-2 text-xs font-bold text-white bg-[#C5A059] hover:bg-[#a8844a] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isPublishing ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Publicando...</>
            ) : (
              <><Send className="w-3.5 h-3.5" /> Publicar Consentimento</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default InformedConsentFactory;
